import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../lib/utils'

/* Ported from @roundhouse/ui. base-ui's <Button> becomes a plain <button>
 * plus Radix's Slot: `render={<Link/>}` there is `asChild` here, which is also
 * what the rest of this app already expects. */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border font-medium whitespace-nowrap transition-colors duration-100 ease-out outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-4',
  {
    variants: {
      variant: {
        default: 'border-fg bg-fg text-bg hover:bg-fg-hover',
        outline: 'border-border bg-bg-elev text-fg hover:border-border-strong hover:bg-bg-subtle',
        ghost: 'border-transparent text-fg-muted hover:bg-bg-subtle hover:text-fg',
        destructive: 'border-transparent bg-err-surface text-err hover:bg-err-surface-hover',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        default: 'h-9 px-3.5 text-base',
        lg: 'h-10 px-5 text-base',
        icon: 'size-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants, type ButtonProps }
